import { motion } from "framer-motion";
import { Link, useNavigate } from "react-router-dom";
import { aboutData, socialLinks, serviceCategories } from "../data/mock";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { ChevronRight, ArrowRight, Users, Target, Eye, Award } from "lucide-react";

// Animated wave background component
const WaveBackground = ({ opacity = 0.03 }) => (
  <div className="absolute inset-0 overflow-hidden pointer-events-none">
    <svg
      className="absolute bottom-0 left-0 w-full"
      viewBox="0 0 1440 320"
      preserveAspectRatio="none"
      style={{ height: "40%", opacity }}
    >
      <motion.path
        fill="url(#aboutWaveGradient)"
        animate={{
          d: [
            "M0,192L48,197.3C96,203,192,213,288,229.3C384,245,480,267,576,250.7C672,235,768,181,864,181.3C960,181,1056,235,1152,234.7C1248,235,1344,181,1392,154.7L1440,128L1440,320L1392,320C1344,320,1248,320,1152,320C1056,320,960,320,864,320C768,320,672,320,576,320C480,320,384,320,288,320C192,320,96,320,48,320L0,320Z",
            "M0,128L48,154.7C96,181,192,235,288,234.7C384,235,480,181,576,181.3C672,181,768,235,864,250.7C960,267,1056,245,1152,229.3C1248,213,1344,203,1392,197.3L1440,192L1440,320L1392,320C1344,320,1248,320,1152,320C1056,320,960,320,864,320C768,320,672,320,576,320C480,320,384,320,288,320C192,320,96,320,48,320L0,320Z",
            "M0,192L48,197.3C96,203,192,213,288,229.3C384,245,480,267,576,250.7C672,235,768,181,864,181.3C960,181,1056,235,1152,234.7C1248,235,1344,181,1392,154.7L1440,128L1440,320L1392,320C1344,320,1248,320,1152,320C1056,320,960,320,864,320C768,320,672,320,576,320C480,320,384,320,288,320C192,320,96,320,48,320L0,320Z",
          ],
        }}
        transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
      />
      <defs>
        <linearGradient id="aboutWaveGradient" x1="0%" y1="0%" x2="100%" y2="0%">
          <stop offset="0%" stopColor="#dc2626" />
          <stop offset="50%" stopColor="#ef4444" />
          <stop offset="100%" stopColor="#dc2626" />
        </linearGradient>
      </defs>
    </svg>
  </div>
);

export default function AboutPage() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen pt-28 pb-20 relative overflow-hidden">
      {/* Wave background */}
      <WaveBackground opacity={0.02} />
      
      <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
        {/* Breadcrumb */}
        <div className="flex items-center gap-2 text-[#a1a1aa] text-sm mb-8">
          <Link to="/" className="hover:text-white transition-colors">Home</Link>
          <ChevronRight className="w-3.5 h-3.5" />
          <span className="text-white">About Us</span>
        </div>

        {/* Hero Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center mb-20"
        >
          <Badge variant="outline" className="border-[#dc2626]/30 bg-[#dc2626]/[0.08] text-[#ef4444] px-4 py-1.5 text-sm rounded-full mb-6">
            About Cube Multimedia
          </Badge>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
            Your Growth Partner for
            <br />
            <span className="text-[#dc2626]">Digital Success</span>
          </h1>
          <p className="text-lg text-[#71717a] max-w-2xl mx-auto">
            {aboutData.tagline}
          </p>
        </motion.div>

        {/* Who We Are */}
        <motion.section
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="mb-20"
        >
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl font-bold text-white mb-6" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
                Who We <span className="text-[#dc2626]">Are</span>
              </h2>
              <p className="text-[#a1a1aa] leading-relaxed mb-6">
                {aboutData.whoWeAre}
              </p>
              <p className="text-[#71717a] leading-relaxed">
                {aboutData.description}
              </p>
            </div>
            <div className="grid grid-cols-2 gap-4">
              {aboutData.stats.map((stat, i) => (
                <motion.div
                  key={stat.label}
                  initial={{ opacity: 0, scale: 0.9 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.1 }}
                  className="p-6 rounded-2xl bg-white/[0.03] border border-white/[0.08] text-center"
                >
                  <div className="text-3xl font-bold text-[#dc2626] mb-2" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
                    {stat.value}
                  </div>
                  <p className="text-[#71717a] text-sm">{stat.label}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </motion.section>

        {/* Mission & Vision */}
        <motion.section
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="mb-20"
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="p-8 rounded-2xl bg-gradient-to-br from-[#dc2626]/10 to-transparent border border-[#dc2626]/20">
              <div className="w-12 h-12 rounded-xl bg-[#dc2626]/20 flex items-center justify-center mb-6">
                <Target className="w-6 h-6 text-[#dc2626]" />
              </div>
              <h3 className="text-xl font-bold text-white mb-4" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
                Our Mission
              </h3>
              <p className="text-[#a1a1aa] leading-relaxed">
                {aboutData.mission}
              </p>
            </div>
            <div className="p-8 rounded-2xl bg-gradient-to-br from-[#a855f7]/10 to-transparent border border-[#a855f7]/20">
              <div className="w-12 h-12 rounded-xl bg-[#a855f7]/20 flex items-center justify-center mb-6">
                <Eye className="w-6 h-6 text-[#a855f7]" />
              </div>
              <h3 className="text-xl font-bold text-white mb-4" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
                Our Vision
              </h3>
              <p className="text-[#a1a1aa] leading-relaxed">
                {aboutData.vision}
              </p>
            </div>
          </div>
        </motion.section>

        {/* Founder Section */}
        <motion.section
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="mb-20"
        >
          <div className="p-10 rounded-3xl bg-white/[0.02] border border-white/[0.06]">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-10 items-center">
              <div className="lg:col-span-1">
                <div className="relative w-48 h-48 mx-auto lg:w-full lg:h-64 rounded-2xl overflow-hidden border border-white/[0.08]">
                  <img
                    src={aboutData.founder.image}
                    alt={aboutData.founder.name}
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>
              <div className="lg:col-span-2 text-center lg:text-left">
                <Badge variant="outline" className="border-[#dc2626]/30 bg-[#dc2626]/[0.08] text-[#ef4444] px-3 py-1 text-xs rounded-full mb-4">
                  Founder & CEO
                </Badge>
                <h3 className="text-2xl font-bold text-white mb-2" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
                  {aboutData.founder.name}
                </h3>
                <p className="text-[#a1a1aa] leading-relaxed mb-6">
                  {aboutData.founder.bio}
                </p>
                <div className="flex flex-wrap gap-4 justify-center lg:justify-start">
                  <div className="px-4 py-2 rounded-lg bg-white/[0.04] border border-white/[0.06]">
                    <span className="text-[#dc2626] font-semibold">{aboutData.founder.experience}</span>
                    <span className="text-[#71717a] text-sm ml-2">Experience</span>
                  </div>
                  <div className="px-4 py-2 rounded-lg bg-white/[0.04] border border-white/[0.06]">
                    <span className="text-[#dc2626] font-semibold">Est. {aboutData.founder.established}</span>
                    <span className="text-[#71717a] text-sm ml-2">Founded</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </motion.section>

        {/* What We Do */}
        <motion.section
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="mb-20"
        >
          <h2 className="text-3xl font-bold text-white mb-10 text-center" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
            What We <span className="text-[#dc2626]">Do</span>
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {serviceCategories.map((cat, i) => (
              <motion.div
                key={cat.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
              >
                <Link
                  to={`/services/${cat.id}`}
                  className="block p-6 rounded-2xl bg-white/[0.03] border border-white/[0.08] hover:border-white/[0.15] transition-all duration-300 group"
                >
                  <div className="w-10 h-10 rounded-lg flex items-center justify-center mb-4" style={{ backgroundColor: `${cat.color}20` }}>
                    <Award className="w-5 h-5" style={{ color: cat.color }} />
                  </div>
                  <h3 className="text-lg font-semibold text-white mb-2 group-hover:text-[#dc2626] transition-colors">
                    {cat.title}
                  </h3>
                  <p className="text-[#71717a] text-sm">{cat.shortDesc}</p>
                </Link>
              </motion.div>
            ))}
          </div>
        </motion.section>

        {/* CTA */}
        <motion.section
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <div className="text-center p-12 rounded-3xl bg-gradient-to-br from-[#dc2626]/10 to-transparent border border-[#dc2626]/20">
            <h2 className="text-2xl lg:text-3xl font-bold text-white mb-4" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
              Ready to Grow Your <span className="text-[#dc2626]">Brand?</span>
            </h2>
            <p className="text-[#71717a] mb-8 max-w-lg mx-auto">
              Let's discuss how Cube Multimedia can help you achieve your marketing goals.
            </p>
            <Button
              onClick={() => navigate("/gmf")}
              className="bg-[#dc2626] hover:bg-[#b91c1c] text-white px-8 py-5 rounded-xl text-sm font-semibold transition-colors duration-300 flex items-center gap-2 mx-auto"
            >
              Book Consultation
              <ArrowRight className="w-4 h-4" />
            </Button>
          </div>
        </motion.section>
      </div>
    </div>
  );
}
